import type { LanguageContent } from './types.js';

// Spanish words will be loaded from external source to avoid bundling
let spanishWords: any[] | null = null;

async function loadSpanishWords(): Promise<any[]> {
  if (typeof window === 'undefined') return [];
  if (!spanishWords) {
    try {
      const response = await fetch('/api/vocabulary/es_v2.json');
      if (response.ok) {
        spanishWords = await response.json();
        return spanishWords!;
      }
    } catch {
      spanishWords = [];
    }
  }
  return spanishWords ?? [];
}

// Helper function to extract vocabulary by CEFR level with high-frequency words prioritized
function extractVocabularyByCEFR(data: any[], cefrLevel: string) {
  if (!Array.isArray(data)) {
    console.warn('Data is not an array:', typeof data);
    return [];
  }
  
  return data
    .filter((word: any) => 
      word.cefr_level === cefrLevel && 
      word.useful_for_flashcard
    )
    .sort((a: any, b: any) => (b.word_frequency || 0) - (a.word_frequency || 0)) // Sort by frequency (highest first)
    .map((word: any) => ({
      term: word.word,
      meaning: word.english_translation
    }));
}

// Function to get vocabulary data asynchronously
async function getVocabularyData() {
  const words = await loadSpanishWords();
  return {
    A1: extractVocabularyByCEFR(words, "A1"), // 620 words - all available
    A2: extractVocabularyByCEFR(words, "A2"), // 1,748 words - all available
    B1: extractVocabularyByCEFR(words, "B1"), // 4,994 words - all available
    B2: extractVocabularyByCEFR(words, "B2"), // 6,450 words - all available
    C1: extractVocabularyByCEFR(words, "C1"), // 2,532 words - all available
    C2: extractVocabularyByCEFR(words, "C2")  // 48 words - all available
  };
}

export const spanishContent: LanguageContent = {
  language: "Spanish",
  code: "es",
  pronouns: [
    "yo",
    "tú",
    "él",
    "ella",
    "usted",
    "nosotros",
    "vosotros",
    "ellos",
    "ellas",
    "ustedes"
  ],
  tenses: ["present"],
  grammar: [
    {
      title: "Regular -ar verbs in present",
      explanation:
        "To conjugate -ar verbs, drop -ar and add: -o, -as, -a, -amos, -áis, -an.",
      examples: [
        "hablar → yo hablo, tú hablas, él/ella habla, nosotros hablamos, vosotros habláis, ellos hablan",
      ],
    },
    {
      title: "Regular -er verbs in present",
      explanation:
        "Drop -er and add: -o, -es, -e, -emos, -éis, -en.",
      examples: ["comer → yo como, tú comes, él come, nosotros comemos, vosotros coméis, ellos comen"],
    },
    {
      title: "Regular -ir verbs in present",
      explanation:
        "Drop -ir and add: -o, -es, -e, -imos, -ís, -en.",
      examples: ["vivir → yo vivo, tú vives, él vive, nosotros vivimos, vosotros vivís, ellos viven"],
    },
    {
      title: "Gender and number",
      explanation:
        "Most nouns ending in -o are masculine, -a feminine. Plural usually adds -s or -es.",
      examples: ["el perro / los perros", "la casa / las casas", "el lápiz / los lápices"],
    },
    {
      title: "Definite articles",
      explanation: "el (m.sg), la (f.sg), los (m.pl), las (f.pl).",
      examples: ["el libro, la mesa, los coches, las flores"],
    },
  ],
  vocabulary: async () => {
    const data = await getVocabularyData();
    return [
      {
        title: "A1 - Beginner",
        words: data.A1,
      },
      {
        title: "A2 - Elementary",
        words: data.A2,
      },
      {
        title: "B1 - Intermediate",
        words: data.B1,
      },
      {
        title: "B2 - Upper Intermediate",
        words: data.B2,
      },
      {
        title: "C1 - Advanced",
        words: data.C1,
      },
      {
        title: "C2 - Proficient",
        words: data.C2,
      },
    ];
  },
  sentences: [
    { prompt: "Yo __ café por la mañana (beber)", answer: "bebo" },
    { prompt: "Nosotros __ en Madrid (vivir)", answer: "vivimos" },
    { prompt: "¿__ tú español? (hablar)", answer: "hablas" },
  ],
  comprehension: {
    paragraph:
      "Sofía vive en Barcelona. Cada día toma el metro para ir al trabajo. Le gusta leer un libro durante el viaje y tomar un café cuando llega a la oficina.",
    questions: [
      { q: "¿Dónde vive Sofía?", a: "En Barcelona." },
      { q: "¿Cómo va al trabajo?", a: "En metro." },
      { q: "¿Qué le gusta hacer durante el viaje?", a: "Leer un libro." },
    ],
  },
  speaking: [
    {
      topic: "Presentarte",
      instruction: "Preséntate en 3-4 frases: nombre, origen, trabajo/estudios, aficiones.",
      starter: "Hola, me llamo... Soy de... Trabajo/Estudio... Me gusta...",
    },
    {
      topic: "En un café",
      instruction: "Imagina que pides en un café. Di lo que quieres y pregunta el precio.",
    },
  ],
  verbs: [
    {
      infinitive: "hablar",
      english: "to speak",
      conjugations: {
        present: {
          yo: "hablo",
          tú: "hablas",
          él: "habla",
          ella: "habla",
          usted: "habla",
          nosotros: "hablamos",
          vosotros: "habláis",
          ellos: "hablan",
          ellas: "hablan",
          ustedes: "hablan",
        },
      },
    },
    {
      infinitive: "comer",
      english: "to eat",
      conjugations: {
        present: {
          yo: "como",
          tú: "comes",
          él: "come",
          ella: "come",
          usted: "come",
          nosotros: "comemos",
          vosotros: "coméis",
          ellos: "comen",
          ellas: "comen",
          ustedes: "comen",
        },
      },
    },
    {
      infinitive: "vivir",
      english: "to live",
      conjugations: {
        present: {
          yo: "vivo",
          tú: "vives",
          él: "vive",
          ella: "vive",
          usted: "vive",
          nosotros: "vivimos",
          vosotros: "vivís",
          ellos: "viven",
          ellas: "viven",
          ustedes: "viven",
        },
      },
    },
    {
      infinitive: "ser",
      english: "to be (essential)",
      conjugations: {
        present: {
          yo: "soy",
          tú: "eres",
          él: "es",
          ella: "es",
          usted: "es",
          nosotros: "somos",
          vosotros: "sois",
          ellos: "son",
          ellas: "son",
          ustedes: "son",
        },
      },
    },
    {
      infinitive: "estar",
      english: "to be (state)",
      conjugations: {
        present: {
          yo: "estoy",
          tú: "estás",
          él: "está",
          ella: "está",
          usted: "está",
          nosotros: "estamos",
          vosotros: "estáis",
          ellos: "están",
          ellas: "están",
          ustedes: "están",
        },
      },
    },
    {
      infinitive: "tener",
      english: "to have",
      conjugations: {
        present: {
          yo: "tengo",
          tú: "tienes",
          él: "tiene",
          ella: "tiene",
          usted: "tiene",
          nosotros: "tenemos",
          vosotros: "tenéis",
          ellos: "tienen",
          ellas: "tienen",
          ustedes: "tienen",
        },
      },
    },
    {
      infinitive: "ir",
      english: "to go",
      conjugations: {
        present: {
          yo: "voy",
          tú: "vas",
          él: "va",
          ella: "va",
          usted: "va",
          nosotros: "vamos",
          vosotros: "vais",
          ellos: "van",
          ellas: "van",
          ustedes: "van",
        },
      },
    },
    {
      infinitive: "poder",
      english: "to be able to",
      conjugations: {
        present: {
          yo: "puedo",
          tú: "puedes",
          él: "puede",
          ella: "puede",
          usted: "puede",
          nosotros: "podemos",
          vosotros: "podéis",
          ellos: "pueden",
          ellas: "pueden",
          ustedes: "pueden",
        },
      },
    },
  ],
};


